import React from 'react';
import {
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CloudDownload as DownloadIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import AppDialog from '../layout/AppDialog';
import useNotification from '../../hooks/useNotification';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import ReactMarkdown from 'react-markdown';

const WorkflowDialog = ({
  open,
  onClose,
  dialogData,
  gitConfig,
  deployConfig,
  onGitConfigChange,
  onDeployConfigChange,
  onDialogDataChange,
  onSubmit
}) => {
  const { showNotification } = useNotification();
  
  // Function to copy content to clipboard
  const handleCopyToClipboard = (content) => {
    if (!content) return;
    
    navigator.clipboard.writeText(content)
      .then(() => {
        showNotification({
          message: 'Content copied to clipboard',
          type: 'success'
        });
      })
      .catch(err => {
        console.error('Failed to copy content:', err);
        showNotification({
          message: 'Failed to copy content to clipboard',
          type: 'error'
        });
      });
  };
  
  // Function to download content as a file
  const handleDownloadFile = (filename, content) => {
    if (!content || !filename) return;
    
    const element = document.createElement('a');
    const file = new Blob([content], {type: 'text/markdown'});
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    showNotification({
      message: `File "${filename}" downloaded successfully`,
      type: 'success'
    });
  };
  const renderDialogContent = () => {
    switch (dialogData.type) {
      case 'viewFile':
        return (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {dialogData.filename || 'File Preview'}
              </Typography>
              <Box>
                <IconButton 
                  onClick={() => handleCopyToClipboard(dialogData.content)}
                  size="small"
                  title="Copy to clipboard"
                >
                  <MenuBookIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  onClick={() => handleDownloadFile(dialogData.filename, dialogData.content)}
                  size="small"
                  title="Download file"
                  sx={{ ml: 1 }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ 
              maxHeight: '60vh', 
              overflow: 'auto', 
              bgcolor: '#f8f9fa', 
              p: 2, 
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <ReactMarkdown>
                {dialogData.content || '*No content available*'}
              </ReactMarkdown>
            </Box>
          </>
        );
      
      case 'extractPRD':
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              The system will analyze your project requirements and extract a structured PRD. 
              You can review and edit the extracted content before proceeding.
            </Alert>
            <CodeMirror
              value={dialogData.content || ''}
              height="400px"
              onChange={(value) => onDialogDataChange({ ...dialogData, content: value })}
              extensions={[markdown()]}
            />
          </>
        );
      
      case 'claudeCode':
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Connect to Claude Code terminal to start development using the PRD.
              All generated documents will be used as reference.
            </Alert>
            <Typography variant="body2" gutterBottom>
              The following documents will be used:
            </Typography>
            <ul>
              <li>PRD Document</li>
              <li>Field Validations</li>
              <li>Technology Stack</li>
              <li>Development Guidelines</li>
              <li>Design Guidelines</li>
              <li>Security Guidelines</li>
              <li>API Guidelines</li>
              <li>Features</li>
              <li>User Stories</li>
            </ul>
          </>
        );
      
      case 'gitSetup':
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Set up a Git repository for your project. The code will be automatically committed and pushed.
            </Alert>
            <TextField
              label="Repository Name"
              fullWidth
              margin="normal"
              value={gitConfig.repoName}
              onChange={(e) => onGitConfigChange({ ...gitConfig, repoName: e.target.value })}
              required
            />
            <TextField
              label="GitLab URL"
              fullWidth
              margin="normal"
              value={gitConfig.gitlabUrl}
              onChange={(e) => onGitConfigChange({ ...gitConfig, gitlabUrl: e.target.value })}
              required
            />
            <TextField
              label="GitLab Token"
              fullWidth
              margin="normal"
              type="password"
              value={gitConfig.gitlabToken}
              onChange={(e) => onGitConfigChange({ ...gitConfig, gitlabToken: e.target.value })}
              required
            />
          </>
        );
      
      case 'deploy':
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Configure deployment settings for your application.
            </Alert>
            <TextField
              label="Domain"
              fullWidth
              margin="normal"
              value={deployConfig.domain}
              onChange={(e) => onDeployConfigChange({ ...deployConfig, domain: e.target.value })}
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Platform</InputLabel>
              <Select
                value={deployConfig.platform}
                onChange={(e) => onDeployConfigChange({ ...deployConfig, platform: e.target.value })}
                label="Platform"
              >
                <MenuItem value="aws">AWS</MenuItem>
                <MenuItem value="gcp">Google Cloud</MenuItem>
                <MenuItem value="azure">Azure</MenuItem>
                <MenuItem value="digitalocean">DigitalOcean</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="API Key"
              fullWidth
              margin="normal"
              type="password"
              value={deployConfig.apiKey}
              onChange={(e) => onDeployConfigChange({ ...deployConfig, apiKey: e.target.value })}
              required
            />
          </>
        );
      
      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    switch (dialogData.type) {
      case 'viewFile':
        return dialogData.title || 'View File';
      case 'extractPRD':
        return 'Extract PRD from Requirements';
      case 'claudeCode':
        return 'Connect to Claude Code';
      case 'gitSetup':
        return 'Set Up Git Repository';
      case 'deploy':
        return 'Deploy Application';
      default:
        return 'PRD Workflow';
    }
  };

  const getSubmitButtonText = () => {
    switch (dialogData.type) {
      case 'viewFile':
        return 'Close';
      case 'extractPRD':
        return 'Extract PRD';
      case 'claudeCode':
        return 'Connect';
      case 'gitSetup':
        return 'Set Up Repository';
      case 'deploy':
        return 'Deploy';
      default:
        return 'Submit';
    }
  };

  const isSubmitDisabled = () => {
    return (
      (dialogData.type === 'extractPRD' && !dialogData.content) ||
      (dialogData.type === 'gitSetup' && (!gitConfig.repoName || !gitConfig.gitlabUrl || !gitConfig.gitlabToken)) ||
      (dialogData.type === 'deploy' && (!deployConfig.domain || !deployConfig.platform || !deployConfig.apiKey))
    );
  };

  return (
    <AppDialog 
      open={open} 
      onClose={onClose}
      title={getDialogTitle()}
      maxWidth={dialogData.type === 'viewFile' ? 'lg' : 'md'}
      primaryActionText={getSubmitButtonText()}
      onPrimaryAction={onSubmit}
      loading={isSubmitDisabled()}
    >
      {renderDialogContent()}
    </AppDialog>
  );
};

export default WorkflowDialog; 